import React, { useState, useEffect } from 'react';
import styles from './ItemImage.module.scss';
import { useParams } from 'react-router-dom';
import { dateOnlyFormatter } from '../../../utils/dateFormatter';
import api from '../../../services/api';
import Loading from '../../Shared/Loading';
import ItemImageListItem from './ItemImageListItem';
import { useTranslation } from 'react-i18next';
import { Form, Row, Col, Button } from 'react-bootstrap';
import TimeSelector from '../TimeSelector';

export default function ItemImageList({ item }) {
  const { t } = useTranslation();
  let { consultationUid, recordUid } = useParams();

  const [data, setData] = useState(item);
  const [length, setLength] = useState(item?.imageList?.lengthSeconds);
  const [startTime, setStartTime] = useState(item?.imageList?.startTimeSeconds || 0);
  const [recordStartDate, setRecordStartDate] = useState(null);
  const [isVisible, setIsVisible] = useState(item?.imageList?.lengthSeconds > 0);

  useEffect(() => {
    // Try to get record start date from the item data or fetch it
    if (item?.recordStartDate) {
      setRecordStartDate(item.recordStartDate);
    } else if (item?.imageList?.recordStartDate) {
      setRecordStartDate(item.imageList.recordStartDate);
    } else if (consultationUid && recordUid) {
      // Try to fetch the record data using the recordUid from URL
      api.get(`/consultation/expert/accepted/${consultationUid}/user/record/${recordUid}`)
        .then((response) => {
          if (response.data?.description?.date) {
            setRecordStartDate(response.data.description.date);
          } else if (response.data?.startDate) {
            setRecordStartDate(response.data.startDate);
          }
        })
        .catch((error) => {
          // If we can't get the record date, the absolute time toggle won't be shown
          // This is the correct behavior - we only show the toggle when we have real data
          console.log('Record date not available - absolute time toggle will not be shown');
        });
    }
    // No fallback - if we don't have real data, we don't show the absolute time option
  }, [consultationUid, recordUid, item]);

  if (data.imageList == null)
    return;

  const handleStartTimeChange = (seconds) => {
    setStartTime(seconds);
    data.imageList.startTimeSeconds = seconds;
  };

  const handleLengthChange = (seconds) => {
    setLength(seconds);
    data.imageList.lengthSeconds = seconds;
  };

  const handleVisibilityToggle = (checked) => {
    setIsVisible(checked);
    if (checked) {
      // When toggling on, set default length to 30 seconds if currently 0
      const newLength = length === 0 ? 30 : length;
      setLength(newLength);
      data.imageList.lengthSeconds = newLength;
    } else {
      // When toggling off, set length to 0 and apply changes immediately
      setLength(0);
      data.imageList.lengthSeconds = 0;
      // Call the API to update the backend
      api
        .post(`consultation/expert/accepted/${consultationUid}/report/item/${data.uid}/recalculate`, data)
        .then((response) => {
          console.log('Visibility toggled off, data updated:', response.data);
          setData(response.data);
        })
        .catch((error) => {
          console.error('Error updating visibility:', error);
        });
    }
  };

  const formatTimeDisplay = (totalSeconds) => {
    if (!totalSeconds && totalSeconds !== 0) return '0s';
    
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
    
    return parts.join(' ');
  };

  const applyChanges = () => {
    console.log(data);
    api
      .post(`consultation/expert/accepted/${consultationUid}/report/item/${data.uid}/recalculate`, data)
      .then((response) => {
        console.log(response.data);
        setData(response.data);
      }, (error) => {
    });
  };

  return (
    <>
      {!data.isReadOnly &&
        <>
        <Row className="align-items-center mb-2">
          <Col xs="auto">
            <Form.Check
              type="switch"
              id={`visibility-switch-${data.uid}`}
              checked={isVisible}
              onChange={(e) => handleVisibilityToggle(e.target.checked)}
              disabled={data.isReadOnly}
              className="me-2"
            />
          </Col>
          <Col>
            <h3 className="d-inline-block mb-0">{data.displayName}</h3>
          </Col>
        </Row>
        {isVisible && (
          <Row className="align-items-center">
            <Col xs={12} md={9}>
              <TimeSelector
                startTimeSeconds={startTime}
                lengthSeconds={length}
                onStartTimeChange={handleStartTimeChange}
                onLengthChange={handleLengthChange}
                disabled={data.isReadOnly || !isVisible}
                recordStartDate={recordStartDate}
                hideZeroOption={true}
              />
            </Col>
            <Col xs="auto">
              <Button className="mb-3" onClick={applyChanges}>{t('consultation.protocol.imageList.apply')}</Button>
            </Col>
          </Row>
        )}
        </>
      }
      {data.isReadOnly &&
        <>
        <div>
          <h3>{data.displayName}</h3>
          {data.imageList.lengthSeconds > 0 && (
            <div>
              <b>{t('consultation.protocol.imageList.startFrom')}:</b> {formatTimeDisplay(data.imageList.startTimeSeconds)}, <b>{t('consultation.protocol.imageList.length')}:</b> {data.imageList.lengthSeconds} {t('consultation.protocol.imageList.seconds')}
            </div>
          )}
        </div>
        </>
      }

     
      {data.imageList.images && data.imageList.lengthSeconds > 0 && data.imageList.images.map((image, index) => (
        <div key={image.url}>
          <ItemImageListItem item={{ image : image }} />
        </div>
      ))}
    </>
  );
}
